import { Link } from "react-router-dom";
import { CalendarDays, MapPin } from "lucide-react";
import { fileUrl, fmtDate } from "@/lib/api";
import { Crest, DemoBadge, StatusPill } from "./kit";

export const TeamCard = ({ team }) => {
  const announced = team.status === "announced";
  return (
    <div data-testid={`team-card-${team.slug}`} className="card-lift group relative overflow-hidden rounded-md border border-white/10 bg-[#112240]">
      <div className="absolute inset-x-0 top-0 h-1" style={{ background: team.primary_color }} />
      <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full opacity-20 blur-2xl transition-opacity duration-500 group-hover:opacity-40" style={{ background: team.primary_color }} />
      <div className="relative flex items-start justify-between p-5">
        <span className="font-score text-xs font-bold tracking-widest text-slate-500">#{String(team.number).padStart(2, "0")}</span>
        <StatusPill tone={announced ? "ok" : "soon"}>{announced ? "Announced" : "Coming Soon"}</StatusPill>
      </div>
      <div className="relative flex justify-center pb-4">
        <Crest number={team.number} color={team.primary_color} logo={fileUrl(team.logo)} name={team.name} />
      </div>
      <div className="relative border-t border-white/10 p-5">
        <h3 className="font-display text-2xl uppercase">{team.name}</h3>
        <p className="mt-1 flex items-center gap-1.5 text-xs uppercase tracking-wider text-slate-400">
          <MapPin className="h-3 w-3" /> {team.city || "To be announced"}
        </p>
        <Link to={`/teams/${team.slug}`} data-testid={`view-team-${team.slug}`} className="mt-4 inline-flex text-xs font-bold uppercase tracking-widest text-[#00E5FF] transition-colors hover:text-white">
          View Team →
        </Link>
      </div>
    </div>
  );
};

const TeamSide = ({ team, inn, align }) => (
  <div className={`flex items-center gap-3 ${align === "right" ? "flex-row-reverse text-right" : ""}`}>
    <Crest size="sm" number={team?.number || 0} color={team?.primary_color} logo={fileUrl(team?.logo)} name={team?.name} />
    <div>
      <p className="font-display text-lg uppercase leading-tight">{team?.name || "TBA"}</p>
      {inn && <p className="font-score text-xl font-bold text-[#00E5FF]">{inn.runs}/{inn.wickets} <span className="text-xs text-slate-400">({inn.overs})</span></p>}
    </div>
  </div>
);

export const MatchCard = ({ match, teams = {}, venues = {} }) => {
  const a = teams[match.team_a_id];
  const b = teams[match.team_b_id];
  const innA = match.innings?.find((i) => i.team_id === match.team_a_id);
  const innB = match.innings?.find((i) => i.team_id === match.team_b_id);
  const tone = match.status === "live" ? "soon" : match.status === "completed" ? "ok" : "mute";
  return (
    <Link to={`/fixtures/match/${match.id}`} data-testid={`match-card-${match.number}`} className="card-lift block rounded-md border border-white/10 bg-[#112240] p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2 text-xs">
        <span className="font-score font-bold uppercase tracking-widest text-slate-400">Match {match.number}{match.stage ? ` · ${match.stage}` : ""}</span>
        <StatusPill tone={tone}>{match.status}</StatusPill>
      </div>
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
        <TeamSide team={a} inn={innA} />
        <span className="font-display text-sm text-slate-500">VS</span>
        <TeamSide team={b} inn={innB} align="right" />
      </div>
      <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1 border-t border-white/10 pt-3 text-xs text-slate-400">
        <span className="flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" />{fmtDate(match.date)} {match.time}</span>
        <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{venues[match.venue_id]?.venue_name || venues[match.venue_id]?.city || "Venue TBA"}</span>
      </div>
      {match.result && <p className="mt-2 text-sm font-semibold text-[#FF8a65]">{match.result}</p>}
    </Link>
  );
};

export const NewsCard = ({ article, featured = false }) => (
  <Link to={`/news/${article.slug}`} data-testid={`news-card-${article.slug}`} className={`card-lift group block overflow-hidden rounded-md border border-white/10 bg-[#112240] ${featured ? "md:col-span-2" : ""}`}>
    <div className={`relative overflow-hidden ${featured ? "aspect-[16/8]" : "aspect-[16/10]"}`}>
      <img src={fileUrl(article.featured_image) || "/images/hero.jpg"} alt={article.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
      <div className="absolute left-3 top-3 flex gap-2">
        <span className="rounded-sm bg-[#0A192F]/90 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-[#00E5FF]">{article.category}</span>
        {article.is_demo && <DemoBadge />}
      </div>
    </div>
    <div className="p-5">
      <p className="font-score text-xs uppercase tracking-widest text-slate-500">{fmtDate(article.published_at)}</p>
      <h3 className={`mt-2 font-display uppercase leading-tight transition-colors group-hover:text-[#00E5FF] ${featured ? "text-3xl" : "text-xl"}`}>{article.title}</h3>
    </div>
  </Link>
);

export const indexBy = (arr = []) => Object.fromEntries((arr || []).map((x) => [x.id, x]));
